import React from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, MapPin, ShieldCheck, Clock, Star, IndianRupee,
  Wrench, Snowflake, BatteryCharging, Disc, PaintBucket, Sparkles,
  CircleDot, Compass, Droplet, AlertTriangle, Square, MessageCircle,
} from "lucide-react";
import { cities, services, brands } from "../../utils/seoData";
import { useSeoHead } from "../../utils/useSeoHead";
import CarServiceHeader from "../../components/carservice/CarServiceHeader";
import CarServiceFooter from "../../components/carservice/CarServiceFooter";

const ICONS = {
  Wrench, Snowflake, BatteryCharging, Disc, PaintBucket, Sparkles,
  CircleDot, Compass, Droplet, ShieldCheck, AlertTriangle, Square,
};

const SITE_ORIGIN = "https://www.wisedrive.com";

/**
 * /car-service — national programmatic SEO hub.
 *
 * Acts as the parent node for every city/service/locality/brand/model page.
 * Crawler-facing internal-link backbone: every leaf page gets here in ≤2 hops.
 */
const CarServiceHub = () => {
  const canonical = `${SITE_ORIGIN}/car-service`;

  useSeoHead({
    title: "Doorstep Car Service in India · From ₹2,499 | Wisedrive",
    description:
      "Doorstep car service across Bangalore, Hyderabad and beyond. OEM parts, certified mechanics, 1,000 km warranty, fixed pricing — book in 60 seconds.",
    canonical,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AutoRepair",
        name: "Wisedrive — Doorstep Car Service",
        url: canonical,
        telephone: "+919905129146",
        priceRange: "From ₹2,499",
        image: `${SITE_ORIGIN}/wisedrive-logo-new.png`,
        address: { "@type": "PostalAddress", addressCountry: "IN" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "3217" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "Car Service", item: canonical },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <CarServiceHeader />

      {/* Hero */}
      <header className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white pt-12 pb-14 sm:pt-16 sm:pb-20 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/10 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-200 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live across {cities.length} cities · {brands.length}+ brands
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]"
            style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
          >
            Doorstep Car Service{" "}<br />
            <span className="text-blue-300">Across India</span>
          </h1>
          <p className="mt-5 text-base sm:text-xl text-blue-100/90 max-w-2xl leading-relaxed">
            Skip the workshop. Our certified mechanic comes to your doorstep with
            OEM parts, fixes your car in 60 minutes, and backs every job with a
            1,000 km warranty. Fixed all-inclusive pricing — zero surprise bills.
          </p>

          <div className="mt-7 flex flex-wrap gap-2">
            <Pill icon={Star}        text="4.8★ · 3,200+ Google reviews" />
            <Pill icon={Clock}       text="Most jobs in 60 min" />
            <Pill icon={ShieldCheck} text="1,000 km warranty" />
            <Pill icon={IndianRupee} text="Fixed all-inclusive pricing" />
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/carservice"
              data-testid="hub-cta-quote"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm sm:text-base shadow-lg transition-all"
            >
              Get Instant Quote
              <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href="https://wa.me/919905129146?text=Hi%2C%20I%20need%20a%20car%20service%20quote."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hub-cta-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-sm sm:text-base transition-all"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp +91 99051 29146
            </a>
          </div>
        </div>
      </header>

      {/* Cities */}
      <section className="px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">
              Coverage
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              Cities we serve
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl">
              Every city, every locality. Pick yours to see local pricing, mechanic availability and same-day slots.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/car-service/${c.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-400 hover:shadow-md transition-all"
                data-testid={`hub-city-${c.slug}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-50 inline-flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-slate-900 text-lg group-hover:text-blue-700">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.state} · {c.localities.length} localities</div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed mt-3">{c.tagline}</p>
                <div className="mt-3 flex items-center text-xs text-blue-600 font-bold gap-1">
                  Book in {c.name} <ChevronRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-5 sm:px-8 py-12 sm:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">
              What we fix
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              {services.length} services covered
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl">
              From a ₹599 wheel alignment to a full denting and ceramic-coating job. Same OEM-grade quality as authorised workshops.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((s) => {
              const Icon = ICONS[s.icon] || Wrench;
              return (
                <div key={s.slug} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-blue-50 inline-flex items-center justify-center">
                      <Icon className="h-4.5 w-4.5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 text-sm leading-tight">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">From ₹{s.fromPrice.toLocaleString("en-IN")} · {s.duration}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {cities.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/car-service/${c.slug}/${s.slug}`}
                            className="text-[11px] text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded-md font-semibold"
                            data-testid={`hub-service-${s.slug}-${c.slug}`}
                          >
                            in {c.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">
              Brands we service
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              {brands.length}+ brands · {brands.reduce((n, b) => n + b.models.length, 0)}+ models
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl">
              Maruti to BMW. Mass-market to luxury — certified mechanics for every brand, OEM-grade parts every time.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => (
              <Link
                key={b.slug}
                to={`/car-service/${cities[0].slug}/general-service/${b.slug}`}
                className="px-4 py-2 rounded-full border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 text-sm font-semibold text-slate-700 hover:text-blue-700 transition-all"
                data-testid={`hub-brand-${b.slug}`}
              >
                {b.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Wisedrive */}
      <section className="px-5 sm:px-8 py-12 sm:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-8 text-center">
            Why drivers across India trust Wisedrive
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <v.icon className="h-7 w-7 text-blue-600 mb-3" />
                <div className="text-base font-bold text-slate-900 mb-1.5">{v.title}</div>
                <div className="text-sm text-slate-600 leading-relaxed">{v.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-5 sm:px-8 py-14 bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            Ready to skip the workshop?
          </h2>
          <p className="text-white/90 mb-7 text-lg">
            Get a fixed-price quote in 60 seconds. Mechanic at your doorstep, today.
          </p>
          <Link
            to="/carservice"
            data-testid="hub-cta-bottom"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-blue-700 font-bold shadow-lg hover:bg-blue-50 transition-all"
          >
            Get Instant Quote
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <CarServiceFooter />
    </div>
  );
};

const VALUE_PROPS = [
  { icon: ShieldCheck, title: "1,000 km warranty",     body: "Every job covered. No fine print, no exclusions on parts or labour." },
  { icon: Clock,       title: "60-min service",        body: "Most jobs done in an hour at your doorstep. No workshop wait." },
  { icon: IndianRupee, title: "Fixed pricing",         body: "All-inclusive quote upfront. Taxes, parts and labour. Zero surprise bills." },
  { icon: Star,        title: "4.8★ · 3,200 reviews",  body: "Real Google reviews. Trained, ID-verified mechanics." },
];

const Pill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold text-blue-50">
    <Icon className="h-3.5 w-3.5 text-blue-300" />
    {text}
  </div>
);

export default CarServiceHub;
